import { StorageValidationError, type DisposableCacheEntry, type StorageAdapter, type StorageCapacityProvider, type StorageEstimate } from "./types.js";
import { assertId } from "./validation.js";

/** Optional browser capability boundary: unavailable/denied values stay unknown. */
export async function readStorageEstimate(provider?: StorageCapacityProvider): Promise<StorageEstimate> {
  const result: StorageEstimate = { approximate: true };
  if (!provider) return result;
  try {
    const estimate = await provider.estimate();
    if (typeof estimate.usage === "number" && Number.isFinite(estimate.usage) && estimate.usage >= 0) result.usageBytes = estimate.usage;
    if (typeof estimate.quota === "number" && Number.isFinite(estimate.quota) && estimate.quota >= 0) result.quotaBytes = estimate.quota;
    if (result.usageBytes !== undefined && result.quotaBytes !== undefined) result.availableBytes = Math.max(0, result.quotaBytes - result.usageBytes);
  } catch { /* Estimates may be denied independently of storage access. */ }
  try { if (provider.persisted) result.persistent = await provider.persisted(); } catch { /* Unknown. */ }
  return result;
}

export async function requestStoragePersistence(provider?: StorageCapacityProvider): Promise<"granted" | "denied" | "unavailable"> {
  if (!provider?.persist) return "unavailable";
  try { return await provider.persist() ? "granted" : "denied"; } catch { return "unavailable"; }
}

export function classifySaveFailure(error: unknown): "conflict" | "quota" | "integrity" | "write-failed" {
  const value = error as { name?: string; code?: string } | null;
  if (value?.name === "QuotaExceededError") return "quota";
  if (value?.code === "STORAGE_CONFLICT") return "conflict";
  if (value?.code === "STORAGE_INTEGRITY") return "integrity";
  return "write-failed";
}

export class DisposableCacheStore {
  readonly #adapter: StorageAdapter;
  readonly #maxEntryBytes: number;
  constructor(adapter: StorageAdapter, maxEntryBytes = 25 * 1024 * 1024) {
    this.#adapter = adapter;
    this.#maxEntryBytes = maxEntryBytes;
  }
  async put(entry: DisposableCacheEntry): Promise<void> {
    assertId(entry.id, "cache.id");
    if (entry.projectId !== undefined) assertId(entry.projectId, "cache.projectId");
    if (!["thumbnail", "preview", "render"].includes(entry.kind) || !(entry.bytes instanceof Uint8Array) || entry.bytes.byteLength > this.#maxEntryBytes || !Number.isFinite(Date.parse(entry.createdAt)) || !Number.isFinite(Date.parse(entry.lastUsedAt))) throw new StorageValidationError("Invalid disposable cache entry");
    await this.#adapter.transaction(["disposable-cache"], "readwrite", (tx) => tx.put("disposable-cache", entry.id, entry));
  }
  async get(id: string): Promise<DisposableCacheEntry | undefined> {
    assertId(id, "cache.id");
    return this.#adapter.transaction(["disposable-cache"], "readonly", (tx) => tx.get("disposable-cache", id));
  }
  /** Explicit cleanup of entries unused since cutoff; active IDs are always retained. */
  async cleanup(unusedBefore: Date, activeIds: ReadonlySet<string> = new Set()): Promise<{ removedIds: string[]; freedBytes: number }> {
    if (!Number.isFinite(unusedBefore.getTime())) throw new StorageValidationError("Invalid cache cutoff");
    return this.#adapter.transaction(["disposable-cache"], "readwrite", async (tx) => {
      const entries = await tx.getAll<DisposableCacheEntry>("disposable-cache");
      const removedIds: string[] = [];
      let freedBytes = 0;
      for (const entry of entries.sort((a, b) => a.id.localeCompare(b.id))) {
        if (!activeIds.has(entry.id) && Date.parse(entry.lastUsedAt) < unusedBefore.getTime()) {
          await tx.delete("disposable-cache", entry.id);
          removedIds.push(entry.id);
          freedBytes += entry.bytes.byteLength;
        }
      }
      return { removedIds, freedBytes };
    });
  }
}
